import { useTransaction } from '../../../context/TransactionContext';
import { useTranslation } from 'react-i18next';
import { usePage } from '../../../context/PageContext';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import AddEditTransactionHeader from './addEditTransactionHeader';
import NoData from '../noData';
import { GeneralEmpty, Slash } from '../../icons';
import SearchInputWithFilters from '../SearchInputWithFilters';
import { Radio, RadioGroup, Spin, Text } from '../../Ant';
import { useFetchMatterPhase } from '../../../hooks/useFetchMatterPhase';
import { IMatterPhase } from '../../../interface/matterPhase';
import { ITransaction } from '../../../interface/transaction';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../../hooks/useEscapeToGoBack';
import SortSelector from '../../sortPopover/sortSelector';
import { ISortOption, IActiveSortState } from '../../../interface/common';
import SkeletonOptionList from '../../common/skeleton/skeletonOptionList';
import CollapsibleListLayout from '../../collapsibleListLayout/collapsibleListLayout';
import { useQueryClient } from '@tanstack/react-query';
import { useDiscardDialog } from '../../../hooks/useDiscardDialog';
import { isTransactionFormChanged } from './isTransactionFormChanged';
import CustomModal from '../../informationPopup/customModal';

const MatterPhase = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { transaction, updateTransaction, initialTransaction } = useTransaction();
  const { setTitle } = usePage();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { showDiscardDialog, promptDiscardIfChanged, handleDiscardConfirm, handleDiscardCancel } =
    useDiscardDialog();
  const [searchValue, setSearchValue] = useState<string>('');
  const matterPhaseSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'name',
        label: t('transaction:selectMatterPhase'),
        type: 'text',
        defaultDirection: 'ASC',
      },
    ],
    [t],
  );

  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'name',
    direction: 'ASC',
  });

  const {
    data: matterPhaseList,
    isLoading: isMatterPhaseLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchMatterPhase({
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    pageSize: 50,
    search: searchValue,
    matterId: transaction?.matterId ?? undefined,
  });
  const refreshMatterPhaseList = () => {
    queryClient.removeQueries({ queryKey: ['matter-phases-list'] });
    return refetch();
  };
  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    promptDiscardIfChanged(
      isTransactionFormChanged(transaction, initialTransaction, !!transaction?.id),
      () => {
        updateTransaction(null);
        goBack(undefined, 2);
      },
    );
  };
  const { ref, inView } = useInView({
    threshold: 0.5, // Trigger when 50% of the target is visible
  });
  const handleTransaction = (matterPhase: IMatterPhase) => {
    const payload = {
      ...transaction,
      matterPhase: matterPhase,
      matterPhaseId: matterPhase.id,
      shouldLoadServerPrice: false,
    } as ITransaction;
    updateTransaction(payload);
    goBackWithSelection();
  };
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && matterPhaseList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('transaction:selectMatterPhase')}
        showCross
      />,
    );
  }, []);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin data-testid="small-indicator" size="small" />;
    }
    return null;
  };
  return (
    <>
      <CollapsibleListLayout
        testId="matter-phase-selection"
        header={
          <SearchInputWithFilters
            autoFocus
            searchTitle={t('transaction:searchMatterPhases')}
            handleSearch={(val) => {
              setSearchValue(val);
            }}
            handleRefresh={refreshMatterPhaseList}
          >
            <SortSelector
              modalLabel={t('common:sorting.title')}
              options={matterPhaseSortOptions}
              onApply={(sort) => setActiveSort(sort)}
              activeSort={activeSort}
            />
          </SearchInputWithFilters>
        }
      >
        {matterPhaseList && matterPhaseList.length > 0 && (
          <RadioGroup
            data-testid="radio-group"
            className="d-flex fd-column gap-10"
            value={transaction?.matterPhaseId}
          >
            {matterPhaseList.map((phase: IMatterPhase) => (
              <Radio
                key={phase.id}
                value={phase.id}
                data-testid={`radio-button-${phase?.name}`}
                onClick={() => {
                  const selectedItem = matterPhaseList.find((item) => item?.id === phase?.id);
                  handleTransaction(selectedItem);
                }}
              >
                <Text $fontsize="13px" $color="title" $weight={500} $lineheight="20px">
                  {phase.name}
                </Text>
              </Radio>
            ))}
          </RadioGroup>
        )}
        {hasNextPage && <div ref={ref} className="loading-indicator" />}
        {(!matterPhaseList ||
          (Object.entries(matterPhaseList).length === 0 && !isMatterPhaseLoading)) && (
          <div className="flex-grow d-flex justify-center items-center">
            <NoData
              header={t('transaction:noMatterPhases')}
              subHeader={t('common:tryChangingSearchCriteria')}
              icon={<GeneralEmpty />}
            />
          </div>
        )}
        {isMatterPhaseLoading ? <SkeletonOptionList /> : renderLoader()}
      </CollapsibleListLayout>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </>
  );
};

export default MatterPhase;
